import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Prism from 'prismjs';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import {
  ThumbsUp, ThumbsDown, Copy, Check, ChevronDown, Pencil,
  Lock, FileText, Wrench, RotateCcw, X as XIcon, CircleSlash,
} from 'lucide-react';
import { MermaidBlock } from './MermaidBlock';
import { CognaraLogo } from '../instrument/CognaraLogo';

// Code block with Prism highlighting
const CodeBlock = memo(({ className, children }) => {
  const ref = useRef(null);
  const lang = className?.replace('language-', '') || 'text';

  useEffect(() => {
    if (ref.current) Prism.highlightElement(ref.current);
  }, [children]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="group relative my-3 rounded-lg overflow-hidden bg-nexus-bg-secondary border border-white/5"
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-nexus-bg-tertiary/30 border-b border-white/5">
        <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-mono">{lang}</span>
        <CopyCodeButton text={String(children).replace(/\n$/, '')} />
      </div>
      <pre className="!bg-transparent !m-0 p-3 overflow-x-auto text-[13px] leading-relaxed">
        <code ref={ref} className={`language-${lang}`}>
          {String(children).replace(/\n$/, '')}
        </code>
      </pre>
    </motion.div>
  );
});

const CopyCodeButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="opacity-0 group-hover:opacity-100 transition-opacity text-nexus-text-tertiary hover:text-nexus-text-primary p-1 rounded"
    >
      {copied ? <Check size={13} className="text-nexus-accent-mint" /> : <Copy size={13} />}
    </button>
  );
};

// Markdown renderer components
const mdComponents = {
  code({ className, children, ...props }) {
    const isInline = !className;
    if (isInline) {
      return <code className="px-1.5 py-0.5 bg-nexus-bg-tertiary/50 rounded text-nexus-accent-mint text-[13px] font-mono" {...props}>{children}</code>;
    }
    const lang = className?.replace('language-', '') || '';
    if (lang === 'mermaid') {
      return <MermaidBlock code={String(children).replace(/\n$/, '')} />;
    }
    return <CodeBlock className={className}>{children}</CodeBlock>;
  },
  p({ children }) { return <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>; },
  ul({ children }) { return <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>; },
  ol({ children }) { return <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>; },
  li({ children }) { return <li className="text-nexus-text-primary/90">{children}</li>; },
  h1({ children }) { return <h1 className="text-lg font-heading font-bold mt-4 mb-2">{children}</h1>; },
  h2({ children }) { return <h2 className="text-base font-heading font-semibold mt-3 mb-2">{children}</h2>; },
  h3({ children }) { return <h3 className="text-sm font-heading font-semibold mt-2 mb-1">{children}</h3>; },
  blockquote({ children }) {
    return <blockquote className="border-l-2 border-nexus-accent-mint/40 pl-3 my-2 text-nexus-text-secondary italic">{children}</blockquote>;
  },
  a({ href, children }) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-nexus-accent-mint underline underline-offset-2 hover:text-nexus-accent-mint/80">{children}</a>;
  },
  table({ children }) {
    return <div className="overflow-x-auto my-2"><table className="w-full text-sm border-collapse">{children}</table></div>;
  },
  th({ children }) { return <th className="border border-white/10 px-3 py-1.5 bg-nexus-bg-tertiary/30 text-left font-medium">{children}</th>; },
  td({ children }) { return <td className="border border-white/10 px-3 py-1.5">{children}</td>; },
};

// Source card
const SourceCard = ({ source, index, type }) => {
  const isFoundation = type === 'foundation';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 300, damping: 25 }}
      className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${
        isFoundation
          ? 'bg-nexus-accent-violet/5 border-nexus-accent-violet/15'
          : 'bg-nexus-accent-mint/5 border-nexus-accent-mint/15'
      }`}
    >
      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
        isFoundation ? 'bg-nexus-accent-violet/15' : 'bg-nexus-accent-mint/15'
      }`}>
        {isFoundation
          ? <Lock size={13} className="text-nexus-accent-violet" />
          : <FileText size={13} className="text-nexus-accent-mint" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${isFoundation ? 'text-nexus-accent-violet' : 'text-nexus-accent-mint'}`}>
          {isFoundation ? 'Domain Expertise' : source.source_file || 'Document'}
        </p>
        <p className="text-[11px] text-nexus-text-tertiary mt-0.5 truncate">
          {source.topic || source.category || (source.content?.slice(0, 80) + '...')}
        </p>
      </div>
    </motion.div>
  );
};

export const NexusChatBubble = memo(({
  role,
  content,
  sources,
  toolCalls,
  isStreaming = false,
  partial = false,
  streamProgress = null,  // { chars, startedAt } for the live ticker
  onFeedback,
  onEditSave,
  onRetry,
}) => {
  const isUser = role === 'user';
  // Strip the inline "— stopped by you" marker — the Stopped badge below
  // is the canonical visual cue for partial assistant messages.
  const displayContent = (partial && !isUser)
    ? (content || '').replace(/\n*_+—\s*stopped by you_*\.?/gi, '').trimEnd()
    : content;
  const [showSources, setShowSources] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content || '');
  const [downModal, setDownModal] = useState(false);
  const [downComment, setDownComment] = useState('');
  const editTaRef = useRef(null);

  useEffect(() => {
    if (isEditing && editTaRef.current) {
      editTaRef.current.focus();
      const len = editTaRef.current.value.length;
      editTaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleCopy = () => {
    navigator.clipboard.writeText(displayContent || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditStart = () => {
    setDraft(content || '');
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setDraft(content || '');
  };

  const handleEditSave = () => {
    const trimmed = (draft || '').trim();
    if (!trimmed || trimmed === (content || '').trim()) {
      setIsEditing(false);
      return;
    }
    onEditSave?.(trimmed);
    setIsEditing(false);
  };

  const handleFeedback = (type) => {
    if (type === 'down') {
      // Open modal to capture optional comment before submitting
      setDownComment('');
      setDownModal(true);
      return;
    }
    setFeedback(type);
    onFeedback?.({ vote: type });
  };

  const submitDownFeedback = () => {
    setFeedback('down');
    onFeedback?.({ vote: 'down', comment: (downComment || '').trim() || null });
    setDownModal(false);
  };

  return (
    <motion.div
      initial={isUser ? { opacity: 0, x: 30 } : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`group flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`chat-msg-${role}`}
    >
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center mt-1 ${
        isUser ? 'bg-gradient-to-br from-nexus-accent-violet to-nexus-accent-blue' : 'bg-gradient-to-br from-nexus-accent-mint to-nexus-accent-blue'
      }`}>
        {isUser
          ? <span className="text-white text-xs font-bold">U</span>
          : <CognaraLogo variant="icon" size={18} style={{ color: '#FFFFFF' }} />
        }
      </div>

      {/* Message content */}
      <div className={`flex-1 max-w-[85%] min-w-0 ${isUser ? 'flex flex-col items-end' : ''}`}>
        <div className={`rounded-2xl px-4 py-3 w-full transition-colors ${
          isUser
            ? 'bg-gradient-to-br from-nexus-accent-violet/25 to-nexus-accent-blue/15 border border-nexus-accent-violet/20 rounded-tr-sm'
            : partial
              ? 'bg-nexus-bg-secondary/40 border border-dashed border-white/15 rounded-tl-sm backdrop-blur-sm opacity-90'
              : 'bg-nexus-bg-secondary/80 border border-nexus-border-subtle rounded-tl-sm backdrop-blur-sm'
        }`}
        data-testid={partial ? 'chat-bubble-partial' : undefined}
        >
          {partial && !isUser && !isEditing && (
            <div className="-mt-1 mb-2 flex items-center gap-1.5">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white/70 bg-white/[0.04] border border-white/15"
                title="Generation was stopped — response is incomplete"
                data-testid="stopped-badge"
              >
                <CircleSlash size={10} strokeWidth={2.5} />
                Stopped
              </span>
            </div>
          )}
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                ref={editTaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.preventDefault(); handleEditCancel(); }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleEditSave(); }
                }}
                rows={Math.min(12, Math.max(2, (draft.match(/\n/g) || []).length + 2))}
                className="w-full bg-nexus-bg-primary/40 border border-nexus-border-subtle rounded-lg px-3 py-2 text-[14px] text-nexus-text-primary leading-relaxed focus:outline-none focus:border-nexus-accent-blue/50 resize-none"
                data-testid="edit-msg-textarea"
              />
              <div className="flex items-center justify-end gap-2 text-[11px]">
                <span className="text-nexus-text-tertiary mr-auto">⌘↵ save · esc cancel</span>
                <button
                  onClick={handleEditCancel}
                  className="px-2.5 py-1 rounded-md text-nexus-text-secondary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 transition-colors"
                  data-testid="edit-msg-cancel"
                >Cancel</button>
                <button
                  onClick={handleEditSave}
                  className="px-3 py-1 rounded-md bg-nexus-accent-blue/20 text-nexus-accent-blue border border-nexus-accent-blue/30 hover:bg-nexus-accent-blue/30 transition-colors font-medium"
                  data-testid="edit-msg-save"
                >Save & resend</button>
              </div>
            </div>
          ) : (
            <>
            <div className="text-[14px] text-nexus-text-primary/95 leading-relaxed">
              {displayContent ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={mdComponents}
                >
                  {displayContent}
                </ReactMarkdown>
              ) : isStreaming ? null : (
                <span className="text-nexus-text-tertiary italic">Empty response</span>
              )}
              {/* Streaming cursor */}
              {isStreaming && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.7, repeat: Infinity, repeatType: 'reverse' }}
                  className="inline-block w-[2px] h-[16px] bg-nexus-accent-mint ml-0.5 align-text-bottom"
                />
              )}
            </div>
            {/* Live "thinking" ticker — kb streamed · elapsed · tok/s. */}
            {isStreaming && streamProgress && (
              <StreamTicker chars={streamProgress.chars} startedAt={streamProgress.startedAt} />
            )}
            </>
          )}
        </div>

        {/* Tool calls display */}
        {!isUser && !isStreaming && toolCalls && toolCalls.length > 0 && (
          <div className="mt-2 space-y-1 border-t border-nexus-border-subtle pt-2">
            {toolCalls.map((tc, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px] text-nexus-text-tertiary">
                <Wrench size={10} className="text-nexus-accent-mint flex-shrink-0" />
                <span>Used <span className="font-medium text-nexus-text-secondary">{tc.tool}</span></span>
              </div>
            ))}
          </div>
        )}

        {/* Sources button + panel */}
        {!isUser && !isStreaming && sources && sources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.2 }}
            className="mt-2 w-full"
          >
            <button
              onClick={() => setShowSources(prev => !prev)}
              className="flex items-center gap-1.5 text-xs text-nexus-text-tertiary hover:text-nexus-text-secondary transition-colors group"
              data-testid="toggle-sources-btn"
            >
              <ChevronDown
                size={12}
                className={`transition-transform ${showSources ? 'rotate-180' : ''}`}
              />
              <span>{sources.length} source{sources.length > 1 ? 's' : ''}</span>
            </button>

            <AnimatePresence>
              {showSources && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="grid gap-1.5 mt-2">
                    {sources.map((src, i) => (
                      <SourceCard key={i} source={src} index={i} type={src.type || 'foundation'} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Actions — copy/edit for user, copy/feedback for assistant */}
        {isUser && !isStreaming && content && !isEditing && (
          <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <ActionButton
              onClick={handleCopy}
              icon={copied ? <Check size={14} className="text-nexus-accent-mint" /> : <Copy size={14} />}
              testid="copy-user-msg-btn"
              label={copied ? 'Copied' : 'Copy'}
            />
            {onEditSave && (
              <ActionButton
                onClick={handleEditStart}
                icon={<Pencil size={14} />}
                testid="edit-user-msg-btn"
                label="Edit"
              />
            )}
          </div>
        )}

        {/* Actions — feedback + copy + retry */}
        {!isUser && !isStreaming && content && (
          <div className="flex items-center gap-1 mt-1.5 opacity-60 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <ActionButton
              onClick={handleCopy}
              icon={copied ? <Check size={14} className="text-nexus-accent-mint" /> : <Copy size={14} />}
              testid="copy-msg-btn"
              label={copied ? 'Copied' : 'Copy'}
            />
            <ActionButton
              onClick={() => handleFeedback('up')}
              active={feedback === 'up'}
              activeColor="text-nexus-accent-mint"
              icon={<ThumbsUp size={14} fill={feedback === 'up' ? 'currentColor' : 'none'} />}
              testid="feedback-up-btn"
              label="Helpful"
            />
            <ActionButton
              onClick={() => handleFeedback('down')}
              active={feedback === 'down'}
              activeColor="text-red-400"
              icon={<ThumbsDown size={14} fill={feedback === 'down' ? 'currentColor' : 'none'} />}
              testid="feedback-down-btn"
              label="Not helpful"
            />
            {onRetry && (
              <ActionButton
                onClick={() => onRetry()}
                icon={<RotateCcw size={14} />}
                testid="retry-msg-btn"
                label="Retry"
              />
            )}
          </div>
        )}

        {/* Negative-feedback comment modal */}
        <AnimatePresence>
          {downModal && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.55)' }}
              onClick={() => setDownModal(false)}
              data-testid="feedback-down-modal"
            >
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.97 }}
                className="w-full max-w-md rounded-2xl p-5 bg-nexus-bg-secondary border border-nexus-border-emphasized shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-nexus-text-primary">What was wrong with this response?</p>
                  <button onClick={() => setDownModal(false)} className="p-1 rounded-md text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50" aria-label="Close">
                    <XIcon size={14} />
                  </button>
                </div>
                <p className="text-[12px] text-nexus-text-tertiary mb-3">Optional — your comment helps us improve future answers.</p>
                <textarea
                  value={downComment}
                  onChange={(e) => setDownComment(e.target.value)}
                  rows={3}
                  placeholder="e.g. Inaccurate, missed context, too generic…"
                  maxLength={2000}
                  className="w-full bg-nexus-bg-primary/40 border border-nexus-border-subtle rounded-lg px-3 py-2 text-[13px] text-nexus-text-primary leading-relaxed focus:outline-none focus:border-nexus-accent-blue/50 resize-none"
                  data-testid="feedback-down-textarea"
                  autoFocus
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setDownModal(false)}
                    className="px-3 py-1.5 rounded-md text-[12px] text-nexus-text-secondary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50"
                    data-testid="feedback-down-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitDownFeedback}
                    className="px-3 py-1.5 rounded-md text-[12px] font-semibold bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25"
                    data-testid="feedback-down-submit"
                  >
                    Submit feedback
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});

const ActionButton = ({ onClick, icon, active, activeColor, testid, label }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`p-1.5 rounded-md transition-colors ${
      active ? activeColor : 'text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50'
    }`}
    data-testid={testid}
  >
    {icon}
  </motion.button>
);

const TYPING_STAGES = [
  { from: 0,     label: 'Reading your question…' },
  { from: 2500,  label: 'Searching knowledge base…' },
  { from: 7000,  label: 'Reasoning through the answer…' },
  { from: 14000, label: 'Drafting response…' },
  { from: 28000, label: 'Still working — complex reasoning…' },
];

export const NexusTypingIndicator = () => {
  const [elapsed, setElapsed] = useState(0);
  const [mountedAt] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - mountedAt), 500);
    return () => clearInterval(id);
  }, [mountedAt]);

  const stage = [...TYPING_STAGES].reverse().find(s => elapsed >= s.from) || TYPING_STAGES[0];
  const seconds = Math.floor(elapsed / 1000);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      className="flex gap-3"
      data-testid="chat-typing-indicator"
    >
      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-nexus-accent-mint to-nexus-accent-blue flex items-center justify-center mt-1">
        <CognaraLogo variant="icon" size={18} style={{ color: '#FFFFFF' }} />
      </div>
      <div className="bg-nexus-bg-secondary/80 border border-nexus-border-subtle rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-3 backdrop-blur-sm">
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              animate={{ y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
              className="w-1.5 h-1.5 rounded-full bg-nexus-accent-mint"
            />
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.span
            key={stage.label}
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -3 }}
            transition={{ duration: 0.25 }}
            className="text-[12.5px] text-nexus-text-secondary"
          >
            {stage.label}
          </motion.span>
        </AnimatePresence>
        <span className="ig-mono text-[10.5px] text-nexus-text-tertiary tabular-nums ml-1">
          {seconds}s
        </span>
      </div>
    </motion.div>
  );
};


// Live "thinking" ticker — renders below a streaming assistant bubble
// with live KB streamed, elapsed seconds, and tokens/sec.
// `startedAt` is a ms epoch captured when streaming began.
const StreamTicker = ({ chars, startedAt }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((v) => v + 1), 500);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0.1, (Date.now() - (startedAt || Date.now())) / 1000);
  const kb = ((chars || 0) / 1024).toFixed(1);
  // Anthropic averages ~4 chars/token — close enough for a live ticker.
  const tokPerSec = ((chars || 0) / 4 / elapsed).toFixed(1);
  return (
    <div
      className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider text-nexus-accent-mint bg-nexus-accent-mint/10 border border-nexus-accent-mint/25"
      data-testid="stream-ticker"
      aria-live="polite"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-nexus-accent-mint opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-nexus-accent-mint" />
      </span>
      <span className="font-semibold">Thinking</span>
      <span className="opacity-60">·</span>
      <span className="tabular-nums">{kb} KB</span>
      <span className="opacity-60">·</span>
      <span className="tabular-nums">{elapsed.toFixed(0)}s</span>
      <span className="opacity-60">·</span>
      <span className="tabular-nums">{tokPerSec} tok/s</span>
    </div>
  );
};
